<template>
  <div class="agent-config-form-container">
    <div class="content-area">
      <div class="page-header">
        <div class="header-left d-flex align-center">
          <v-btn
            icon
            variant="text"
            class="back-btn mr-2"
            :to="{ name: 'AgentConfigs' }">
            <Icon name="arrow-left" variant="solid" size="20" color="default"/>
          </v-btn>
          <h1 class="page-title">
            {{ isEdit ? 'Edit Agent Config' : 'Create Agent Config' }}
          </h1>
        </div>
      </div>

      <div class="main-content">
        <div v-if="loading" class="py-12 text-center">
          <v-progress-circular indeterminate color="primary" size="40"/>
        </div>
        <div v-else class="form-layout">
          <div class="left-panel">
            <div class="form-group">
              <label class="input-label">External ID</label>
              <MankTextField
                v-model="form.external_id"
                dense
                hide-details="auto"
                :error-messages="externalIdErrors"
                placeholder="e.g. sales-agent-prod"/>
            </div>
            <div class="form-group">
              <label class="input-label">Description</label>
              <MankTextarea
                v-model="form.description"
                rows="4"
                placeholder="Describe what this config is used for..."/>
            </div>
          </div>
          <div class="right-panel">
            <label class="input-label">Config (YAML) <span class="required">*</span></label>
            <MankTextarea
              v-model="form.config"
              class="yaml-editor"
              no-resize
              :rows="24"
              hide-details="auto"
              :error-messages="yamlErrors"
              :placeholder="yamlPlaceholder"/>
          </div>
        </div>
      </div>

      <div class="footer-buttons">
        <v-btn variant="text" size="medium" class="mank-btn" elevation="0" @click="cancel">
          <Icon name="cross" variant="solid" size="20" color="default"/>
          Cancel
        </v-btn>
        <v-btn
          variant="flat"
          color="primary"
          size="medium"
          class="mank-btn"
          elevation="0"
          :loading="saving"
          :disabled="!canSubmit"
          @click="submit">
          {{ isEdit ? 'Save changes' : 'Create' }}
        </v-btn>
      </div>
    </div>
  </div>
</template>

<script>
import yaml from 'js-yaml';
import Icon from '@/components/Icon.vue';
import MankTextarea from '@/components/MankTextarea.vue';
import MankTextField from '@/components/MankTextField.vue';
import AgentConfigService from '@/services/AgentConfigService';

const emptyForm = () => ({
  external_id: '',
  description: '',
  config: '',
});

export default {
  name: 'AgentConfigForm',
  components: { Icon, MankTextarea, MankTextField },
  props: {
    externalId: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      loading: false,
      saving: false,
      form: emptyForm(),
    };
  },
  computed: {
    isEdit() {
      return Boolean(this.externalId);
    },
    yamlErrors() {
      if (!this.form.config || !this.form.config.trim()) {
        return ['Config is required'];
      }
      try {
        yaml.load(this.form.config);
        return [];
      } catch (e) {
        return [e.message];
      }
    },
    canSubmit() {
      return this.yamlErrors.length === 0 && this.externalIdErrors.length === 0;
    },
    yamlPlaceholder() {
      return 'version: 1\nvoice: en-US\ntemperature: 0.2';
    },
    externalIdErrors() {
      const value = (this.form.external_id || '').trim();
      if (!value) return [];

      if (/\s/.test(value)) {
        return ['External ID cannot contain spaces.'];
      }

      if (!/^[a-zA-Z0-9-]+$/.test(value)) {
        return ['External ID can only contain letters, numbers, and "-".'];
      }

      return [];
    },
  },
  mounted() {
    if (this.isEdit) {
      this.fetchDetail();
    }
  },
  methods: {
    cancel() {
      this.$router.push({ name: 'AgentConfigs' });
    },
    async fetchDetail() {
      this.loading = true;
      try {
        const res = await AgentConfigService.getAgentConfigByExternalId(this.externalId);
        if (res?.success && res.data) {
          this.form = {
            external_id: res.data.external_id || '',
            description: res.data.description || '',
            config: res.data.config || '',
          };
        }
      } catch (e) {
        this.$store.dispatch('ui/showNotification', {
          type: 'error',
          message: e?.message || 'Config detail could not be loaded.',
        }, { root: true });
      } finally {
        this.loading = false;
      }
    },
    async submit() {
      if (!this.canSubmit) return;
      this.saving = true;
      try {
        const payload = {
          external_id: this.form.external_id.trim(),
          description: (this.form.description || '').trim(),
          config: this.form.config,
        };
        if (this.isEdit) {
          await AgentConfigService.updateAgentConfigByExternalId(this.externalId, payload);
        } else {
          await AgentConfigService.createAgentConfig(payload);
        }
        this.$store.dispatch('ui/showNotification', {
          type: 'success',
          message: this.isEdit ? 'Agent config updated.' : 'Agent config created.',
        }, { root: true });
        this.$router.push({ name: 'AgentConfigs' });
      } catch (e) {
        this.$store.dispatch('ui/showNotification', {
          type: 'error',
          message: e?.message || 'Save failed.',
        }, { root: true });
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/styles/variables.scss';
@import '@/styles/colors.scss';

.main-content {
  padding: 24px;
  overflow-y: auto;
}

.form-layout {
  display: flex;
  gap: 24px;
}

.left-panel {
  flex: 0 0 360px;
}

.right-panel {
  flex: 1;
}

.form-group {
  margin-bottom: 20px;
}

.input-label {
  display: block;
  font-size: $text-size-13;
  font-weight: 600;
  margin-bottom: 8px;
}

.required {
  color: $light-primary;
}

.yaml-editor {
  ::v-deep textarea {
    font-family: Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace;
    line-height: 1.5;
    font-size: 13px;
    overflow-y: auto;
  }
}

.footer-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 12px 24px;
  border-top: 1px solid $light-border;
  background-color: $light-surface-2;
}
</style>
